// Pricing migration 32 (ADO-3985), T13, T17 and T19: one multi-buy deal, as Figma's Multi-buy
// Dialog draws it, at each of its sizes:
//   dialog   the centred dialog (06 to 10; tablet the same at Touch sizes, t/06);
//   sheet    the phone's bottom sheet (m/06: the free box stacked, each stepper under its name,
//            the list scrolling and Done pinned);
//   panel    the sheet's layout inside the side cart (15: "Multi-buy Dialog (Size=Mobile)") and on
//            the phone's product page (A3: "the red-header deal panel with Done");
//   section  the product page's deal section from 768px (d/05, t/05): no red header, no Done.
//
// What it says comes from `dealView` (the server's progress, with D-2's recorded threshold
// readings). The header is R5's (06n, 06n-long; the Multi-buy Dialog's "Header title" off and
// "Deal name" on): a slim red band with only the date tag and ✕, so a long capitalised name never
// fills the red area, then at the top of the body the deal's name (the feed's description, free
// text up to SL1.0's 250 characters) in heading/h4, two lines then "…" with the whole name kept for
// screen readers and on hover, the tier under it in promotion red, and the lines' subtitle. A deal
// with no description keeps the tier text as its title. Tablet and phone follow the 28 Oct dialog
// and sheet, as the other 30 Oct boards do.
// The lines' steppers change the cart at once through the basket store (06: "Steppers change the
// cart immediately"; 08: "No Update cart step"); a failed change rolls back with the store's
// message. Out-of-stock lines come last with an "Unavailable" tag (R6 07u).

import { useId } from 'react';
import type { MultibuyDeal } from '../api/multibuy-deals.client';
import { ProductImage } from '../app/Browse/ProductImage';
import { useBasketStore } from '../basket/useBasketStore';
import { isAtLineMaximum, sanitiseQuantityInput, typedLineQuantity } from '../basket/lineQuantity';
import { useIsWowEntry } from '../basket/wowEntry';
import {
  Banner,
  Button,
  Icon,
  QuantityStepper,
  StatusBadge,
  type QuantityStepperCommit,
} from '../design-system';
import { dealView, type DealMeter, type DealViewProgress } from './dealView';
import { dealOfferText } from './multibuyDealText';
import { spokenUnavailableLine } from './spokenDealText';
import { useDealLines, type DealLine } from './useDealLines';
import { useDealSessionStore } from './useDealSessionStore';
import styles from './MultibuyDealView.module.css';

export type DealViewLayout = 'dialog' | 'sheet' | 'panel' | 'section';

export interface MultibuyDealViewProps {
  readonly deal: MultibuyDeal;
  readonly layout: DealViewLayout;
  /** Touch-sized steppers and buttons (tablet and phone, t/06, m/06). */
  readonly touch?: boolean;
  /** The product's other deals: "Also on this product" (R4 07o). */
  readonly otherDeals?: readonly MultibuyDeal[];
  readonly onShowDeal?: (offerCode: string) => void;
  /** Draws Done (06: "Done just closes"). */
  readonly onDone?: () => void;
  /** Draws the header's ✕ (the dialog and the sheet). */
  readonly onClose?: () => void;
}

/** "Meal deal · Buy 2, get 1 free" for a deal with a description, else its offer text. */
export function otherDealLabel(deal: MultibuyDeal): string {
  const offer = dealOfferText(deal);
  return deal.description === undefined || deal.description === offer
    ? offer
    : `${deal.description} · ${offer}`;
}

function Meter({ progress }: { readonly progress: DealViewProgress }) {
  const { meter, chip } = progress;
  if (meter === null || chip === null) return null;
  return (
    <div className={styles.meter} data-part="meter" aria-hidden="true">
      <MeterTrack meter={meter} />
      <span className={styles.plus}>+</span>
      <span className={styles.chip} data-part="chip" data-earned={chip.earned ? '' : undefined}>
        <Icon name="gift" size={16} />
        {chip.label}
      </span>
    </div>
  );
}

function MeterTrack({ meter }: { readonly meter: DealMeter }) {
  if (meter.kind === 'bar') {
    return (
      <span className={styles.bar} data-part="bar">
        <span className={styles.barFill} style={{ width: `${Math.round(meter.fill * 100)}%` }} />
      </span>
    );
  }
  return (
    <span className={styles.slots}>
      {Array.from({ length: meter.count }, (_, index) => {
        const filled = index < meter.filled;
        return (
          <span key={index} className={styles.slot} data-slot={filled ? 'filled' : 'empty'}>
            {filled ? <Icon name="check" size={20} /> : null}
          </span>
        );
      })}
    </span>
  );
}

function LineStepper({ line, touch }: { readonly line: DealLine; readonly touch: boolean }) {
  const add = useBasketStore((s) => s.add);
  const setQuantity = useBasketStore((s) => s.setQuantity);
  const busy = useBasketStore((s) => s.pending.has(line.skuCode));
  const { skuCode, quantity, name } = line;
  const change = (next: number) => {
    if (quantity === 0) {
      if (next > 0) void add(skuCode, next);
      return;
    }
    void setQuantity(skuCode, next);
  };
  const commitTyped = ({ typed }: QuantityStepperCommit) => {
    const next = typedLineQuantity(typed, quantity);
    if (next !== null) change(next);
  };
  return (
    <QuantityStepper
      value={quantity}
      label={`Quantity for ${name}`}
      decreaseLabel={`Decrease quantity of ${name}`}
      increaseLabel={`Increase quantity of ${name}`}
      onDecrease={() => change(quantity - 1)}
      onIncrease={() => change(quantity + 1)}
      decreaseDisabled={quantity === 0}
      increaseDisabled={isAtLineMaximum(quantity)}
      busy={busy}
      size={touch ? 'touch' : 'default'}
      editable={{
        inputLabel: `Quantity of ${name}`,
        sanitiseInput: sanitiseQuantityInput,
        onCommit: commitTyped,
      }}
    />
  );
}

function LineError({ skuCode }: { readonly skuCode: string }) {
  const message = useBasketStore((s) => s.mutationErrors.get(skuCode) ?? null);
  return (
    <p className={styles.lineError} aria-live="polite">
      {message}
    </p>
  );
}

function DealLineItem({ line, touch }: { readonly line: DealLine; readonly touch: boolean }) {
  return (
    <li
      className={styles.line}
      data-unavailable={line.unavailable ? '' : undefined}
      data-sku={line.skuCode}
    >
      <span className={styles.lineImage} aria-hidden="true">
        <ProductImage src={line.imageUrl} alt={line.name} />
      </span>
      <span className={styles.lineText}>
        {line.unavailable ? (
          <>
            <span className={styles.lineName} data-part="name" aria-hidden="true">
              {line.name}
            </span>
            <span className={styles.visuallyHidden}>{spokenUnavailableLine(line.name)}</span>
          </>
        ) : (
          <span className={styles.lineName} data-part="name">
            {line.name}
          </span>
        )}
        <span className={styles.lineCode}>{line.skuCode}</span>
        {line.alsoFree ? <span className={styles.alsoFree}>Also the free product</span> : null}
      </span>
      {line.unavailable ? (
        <span className={styles.unavailable} aria-hidden="true">
          Unavailable
        </span>
      ) : (
        <span className={styles.lineControl}>
          <LineStepper line={line} touch={touch} />
          <LineError skuCode={line.skuCode} />
        </span>
      )}
    </li>
  );
}

export function MultibuyDealView({
  deal,
  layout,
  touch = false,
  otherDeals = [],
  onShowDeal,
  onDone,
  onClose,
}: MultibuyDealViewProps) {
  const wow = useIsWowEntry();
  const lost = useDealSessionStore((s) => s.lost.has(deal.multibuyOfferCode));
  const dealLines = useDealLines(deal);
  const view = dealView(deal, { wow, lost, availableLines: dealLines.availableLines });
  const otherDealsId = useId();
  const banded = layout !== 'section';
  const stacked = layout === 'sheet' || layout === 'panel';
  const subtitle = layout === 'panel' ? view.header.panelSubtitle : view.header.subtitle;
  const loadingLines = dealLines.loading && dealLines.lines.length === 0;
  const { freeProduct } = dealLines;

  return (
    <div className={styles.view} data-layout={layout} data-stacked={stacked ? '' : undefined}>
      {banded ? (
        <div className={styles.band} data-part="band">
          <span className={styles.datePill}>{view.header.pill}</span>
          {onClose === undefined ? null : (
            <button type="button" className={styles.close} aria-label="Close" onClick={onClose}>
              <Icon name="x" size={20} />
            </button>
          )}
        </div>
      ) : null}

      <div className={styles.body} data-part="body">
        {banded ? (
          <div className={styles.dealHead}>
            {layout === 'panel' ? (
              <h3 className={styles.dealName} data-part="deal-name" title={view.header.title}>
                {view.header.title}
              </h3>
            ) : (
              // The Modal's hidden heading names the dialog and the sheet with the whole name, so
              // the visible line is not heard a second time.
              <p
                className={styles.dealName}
                data-part="deal-name"
                title={view.header.title}
                aria-hidden="true"
              >
                {view.header.title}
              </p>
            )}
            {view.header.tiersLine === null ? null : (
              <p className={styles.dealTiers} data-part="deal-tiers">
                {view.header.tiersLine}
              </p>
            )}
            {subtitle === null ? null : <p className={styles.dealSubtitle}>{subtitle}</p>}
          </div>
        ) : null}
        {freeProduct !== null && view.freeBox !== null ? (
          <div
            className={styles.freeBox}
            data-part="free-product"
            data-reached={view.freeBox.reached ? '' : undefined}
          >
            <span className={styles.freeBoxImage} aria-hidden="true">
              <ProductImage src={freeProduct.imageUrl} alt={freeProduct.name} />
            </span>
            <span className={styles.freeBoxText}>
              <span className={styles.freeBoxName}>{freeProduct.name}</span>
              <span className={styles.freeBoxLine}>
                {`${freeProduct.skuCode} · ${view.freeBox.line}`}
              </span>
            </span>
            <span className={styles.freeBoxLabel}>
              <Icon name="gift" size={14} />
              Free product
            </span>
          </div>
        ) : null}

        {view.progress === null ? null : (
          <div className={styles.progress}>
            <p className={styles.message} data-tone={view.progress.tone} aria-live="polite">
              {view.progress.message}
            </p>
            <Meter progress={view.progress} />
            {view.tag === null ? null : (
              <span className={styles.tag}>
                <StatusBadge tone="success" dot={false}>
                  <Icon name="check" size={14} />
                  {view.tag.label}
                </StatusBadge>
              </span>
            )}
            {view.banner === null ? null : (
              <div className={styles.note}>
                <Banner type="info">{view.banner}</Banner>
              </div>
            )}
          </div>
        )}

        {view.listLabel === null ? null : <p className={styles.listLabel}>{view.listLabel}</p>}
        {loadingLines ? (
          <p className={styles.loading} aria-live="polite">
            Loading the deal…
          </p>
        ) : (
          <ul className={styles.lines}>
            {dealLines.lines.map((line) => (
              <DealLineItem key={line.skuCode} line={line} touch={touch || stacked} />
            ))}
          </ul>
        )}
        {view.hint === null ? null : <p className={styles.hint}>{view.hint}</p>}

        {otherDeals.length === 0 ? null : (
          <div className={styles.otherDeals}>
            <p id={otherDealsId} className={styles.otherDealsLabel}>
              Also on this product
            </p>
            <ul className={styles.otherDealsList} aria-labelledby={otherDealsId}>
              {otherDeals.map((other) => (
                <li key={other.multibuyOfferCode}>
                  <button
                    type="button"
                    className={styles.otherDeal}
                    onClick={() => onShowDeal?.(other.multibuyOfferCode)}
                  >
                    {otherDealLabel(other)}
                    <Icon name="arrowRight" size={14} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <p className={styles.picked}>{view.footer}</p>
        {onDone === undefined ? null : (
          <Button variant="primary" className={styles.done} onClick={onDone}>
            Done
          </Button>
        )}
      </div>
    </div>
  );
}
